import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  SizingKitShopifyMap,
  RingProSizingKitShopifyMap,
} from 'content/products/ring/productMap/constants';
import { RareSizingKitShopifyMap } from 'content/products/rare/productMap/constants';
import { SizingKitProductShopifyMap } from 'content/products/accessories/SizingKitProductShopifyMap';
import { generateBundleId } from 'components/shared/Cart/bundle';
import { CartPolicy } from './types';
import { getAttr, pass } from './cartLineHelpers';

const VARIANT_GID = /^gid:\/\/shopify\/ProductVariant\/\d+$/;

const variantId = (
  entry: Record<string, string | number> | undefined,
): string | null => {
  const id = entry?.['shopifyVariantId'];
  // Staging placeholders are the bare gid prefix — never a real line.
  return typeof id === 'string' && VARIANT_GID.test(id) ? id : null;
};

/** Paid standalone kit excluded BY ID: non-PRODUCTION aliases it into
 * RingProSizingKitShopifyMap, and it is a legitimate solo purchase. */
function freeSizingKitIds(store: ShopifyStore): Set<string> {
  const paidKitId = variantId(SizingKitProductShopifyMap[store]);
  return new Set(
    [
      variantId(SizingKitShopifyMap[store]),
      variantId(RingProSizingKitShopifyMap[store]),
      variantId(RareSizingKitShopifyMap[store]),
    ].filter((id): id is string => Boolean(id) && id !== paidKitId),
  );
}

/** Kit + product share one `_bundle_id`, product carries `_is_bundle_parent`;
 * already-tagged callers are untouched. docs/cart-sizing-kit-policy.md */
export const sizingKitBundlePolicy: CartPolicy = (input, _cart, meta) => {
  const lines = input.lineItemsToAdd;
  if (!lines || lines.length < 2 || !meta.store) return pass(input);
  if (lines.some((line) => getAttr(line.attributes, '_bundle_id'))) {
    return pass(input, 'caller already tagged the bundle');
  }

  const kitIds = freeSizingKitIds(meta.store);
  if (!lines.some((line) => kitIds.has(line.merchandiseId))) return pass(input);

  // Parent is the last non-kit line, matching the universal builder convention.
  const parentIndex = lines.reduce(
    (last, line, i) => (kitIds.has(line.merchandiseId) ? last : i),
    -1,
  );
  if (parentIndex === -1) return pass(input, 'kit-only add: no product to parent');

  const bundleId = generateBundleId();
  const lineItemsToAdd = lines.map((line, i) => {
    const isParent = i === parentIndex;
    if (!isParent && !kitIds.has(line.merchandiseId)) return line;
    return {
      ...line,
      attributes: [
        { key: '_bundle_id', value: bundleId },
        ...(isParent ? [{ key: '_is_bundle_parent', value: '1' }] : []),
        ...(line.attributes ?? []),
      ],
    };
  });

  return {
    input: { ...input, lineItemsToAdd },
    reasons: ['grouped free sizing kit with its product'],
  };
};
